/**
 * Cliente HTTP con renovacion automatica de sesion.
 *
 * El access token vive SOLO en memoria: si se guardara en localStorage seguiria
 * ahi despues de cerrar la app y cualquier script podria leerlo. La sesion se
 * recupera al arrancar con la cookie de refresco, que es httpOnly y el
 * JavaScript no puede tocar.
 */
let accessToken: string | null = null
let refreshInFlight: Promise<boolean> | null = null

export function setAccessToken(token: string | null): void {
  accessToken = token
}

export function getAccessToken(): string | null {
  return accessToken
}

export class ApiError extends Error {
  status: number
  code: string
  details?: unknown

  constructor(status: number, message: string, code = 'error', details?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }
}

async function parse(res: Response): Promise<any> {
  const text = await res.text()
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

/**
 * Renueva el access token. Se comparte una unica promesa en vuelo para que
 * varias peticiones que caducan a la vez no disparen tres refrescos en paralelo
 * (el ultimo invalidaria los tokens de los anteriores).
 */
async function refresh(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const res = await fetch('/api/auth/refresh', { method: 'POST', credentials: 'same-origin' })
      if (!res.ok) return false
      const data = await parse(res)
      accessToken = data?.accessToken ?? null
      return Boolean(accessToken)
    } catch {
      return false
    } finally {
      // Se libera en el siguiente tick para que los que esperan vean el token.
      setTimeout(() => {
        refreshInFlight = null
      }, 0)
    }
  })()
  return refreshInFlight
}

async function raw(method: string, path: string, body?: unknown): Promise<Response> {
  return fetch(path, {
    method,
    credentials: 'same-origin',
    headers: {
      ...(body === undefined ? {} : { 'content-type': 'application/json' }),
      ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  })
}

export async function api<T = any>(method: string, path: string, body?: unknown): Promise<T> {
  let res = await raw(method, path, body)

  if (res.status === 401 && !path.startsWith('/api/auth/')) {
    if (await refresh()) {
      res = await raw(method, path, body)
    }
  }

  const data = await parse(res)
  if (!res.ok) {
    throw new ApiError(
      res.status,
      data?.message ?? `Error ${res.status}`,
      data?.error ?? 'error',
      data?.details,
    )
  }
  return data as T
}

export const get = <T = any>(path: string) => api<T>('GET', path)
export const post = <T = any>(path: string, body?: unknown) => api<T>('POST', path, body)
export const patch = <T = any>(path: string, body?: unknown) => api<T>('PATCH', path, body)
export const del = <T = any>(path: string) => api<T>('DELETE', path)

/**
 * Descarga binaria con la misma renovación de sesión que el resto.
 *
 * Las fotos se sirven por un endpoint autenticado, y una etiqueta <img> no
 * puede enviar la cabecera Authorization: hay que traerlas con fetch y crear
 * una URL de objeto.
 */
export async function getBlob(path: string): Promise<Blob> {
  let res = await raw('GET', path)
  if (res.status === 401 && (await refresh())) {
    res = await raw('GET', path)
  }
  if (!res.ok) {
    const data = await parse(res)
    throw new ApiError(res.status, data?.message ?? `Error ${res.status}`, data?.error ?? 'error')
  }
  return res.blob()
}

/** Intenta recuperar la sesion al arrancar la app. */
export async function restoreSession(): Promise<boolean> {
  return refresh()
}
